import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../../components/ui/dropdown-menu";
import { useAuth } from "../../../../context/AuthContext";
import { useAccountingPermissions } from "./shared";
import accountingNav, { ACCOUNTING_BASE } from "./accountingNav";

// PHASE 19: an item can declare a `permission` key (one of the nine
// accounting permissions, or the special "__owner_only__" for the one
// Administration page - Auditor/CA Access - that stays admin/client-only on
// the backend regardless of permission). Absent, an item is shown to
// anyone who can reach the Accounting module at all (unchanged from before
// this phase) - only the items that are genuinely single-purpose
// administrative actions were given a permission tag.
function itemVisible(item, perms, role) {
  if (!item.permission) return true;
  if (item.permission === "__owner_only__") return role === "admin" || role === "client";
  return perms.has(item.permission);
}

function buildBreadcrumb(pathname) {
  for (const entry of accountingNav) {
    if (entry.type === "link" && entry.to === pathname) {
      return { group: null, label: entry.label };
    }
    if (entry.type === "dropdown") {
      const match = entry.items.find((item) => item.to === pathname);
      if (match) return { group: entry.group, label: match.label };
    }
  }
  return { group: null, label: "Accounting" };
}

function isGroupActive(entry, pathname) {
  if (entry.type === "link") return entry.to === pathname;
  return entry.items.some((item) => item.to === pathname);
}

const triggerBase = "inline-flex items-center gap-1 rounded-sm px-2.5 py-1.5 text-xs font-semibold transition-colors";
const triggerActive = "bg-[#111827] text-white";
const triggerInactive = "text-[#374151] hover:bg-[#F3F4F6]";

export default function AccountingShell() {
  const location = useLocation();
  const { user } = useAuth();
  const perms = useAccountingPermissions();
  const currentPath = location.pathname.replace(/\/$/, "") || `${ACCOUNTING_BASE}/dashboard`;
  const breadcrumb = buildBreadcrumb(currentPath);
  const useWideWorkspace = currentPath === `${ACCOUNTING_BASE}/quick-member-receipts`;

  return (
    <div className="accounting-shell-root min-h-screen bg-[#F8FAFC]">
      <div className="accounting-shell-chrome border-b border-[#E5E7EB] bg-white">
        <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <Link to="/client/society" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] hover:text-[#111827]">
                &larr; Society Panel
              </Link>
              <span className="hidden text-[#D1D5DB] sm:inline">|</span>
              <h1 className="font-display text-lg font-extrabold tracking-tight text-[#111827] sm:text-xl">Accounting</h1>
            </div>
            <div className="text-xs font-semibold text-[#6B7280]">
              {breadcrumb.group ? `${breadcrumb.group} / ${breadcrumb.label}` : breadcrumb.label}
            </div>
          </div>

          {/* Compact grouped navigation - dropdowns instead of one button per page. */}
          <nav className="mt-2 flex flex-wrap items-center gap-1 overflow-x-auto">
            {accountingNav.map((entry) => {
              if (entry.type === "link") {
                if (!itemVisible(entry, perms, user?.role)) return null;
                return (
                  <NavLink
                    key={entry.label}
                    to={entry.to}
                    className={({ isActive }) => `${triggerBase} ${isActive ? triggerActive : triggerInactive}`}
                  >
                    {entry.label}
                  </NavLink>
                );
              }
              const visibleItems = entry.items.filter((item) => itemVisible(item, perms, user?.role));
              if (visibleItems.length === 0) return null;
              const active = isGroupActive(entry, currentPath);
              return (
                <DropdownMenu key={entry.group}>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className={`${triggerBase} ${active ? triggerActive : triggerInactive}`}>
                      {entry.group}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[240px] bg-white">
                    {visibleItems.map((item) => {
                      const itemActive = item.to === currentPath;
                      return (
                        <DropdownMenuItem asChild key={`${entry.group}-${item.label}`}>
                          <Link
                            to={item.to}
                            className={`w-full cursor-pointer ${itemActive ? "font-bold text-[#111827]" : "text-[#374151]"}`}
                          >
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </nav>
        </div>
      </div>

      <div className={`accounting-shell-content mx-auto w-full px-4 py-6 sm:px-6 ${useWideWorkspace ? "max-w-none" : "max-w-[1400px]"}`}>
        <Outlet />
      </div>
    </div>
  );
}
